import { create } from "zustand"
import { immer } from "zustand/middleware/immer"
import { z } from "zod"
import Mixpanel from "@/src/analytics/mixpanel"
import { CURRENT_WORKSPACE_ID_STORAGE_KEY, CURRENT_WORKSPACE_ROLE_STORAGE_KEY } from "@/src/utils/constants"
import { httpClient as axios, apiUrls, getTokenFromRefresh } from "@/src/api"
import { setSession, isValidToken } from "@/src/auth/context/jwt/utils"
import type { UserType, WorkspaceInfo } from "@/src/auth/types"
import { ROLES, USER_ROLES } from "@/src/features/internalUsers/constants"
import { applyWorkspaceMeToUser, getWorkspaceMe } from "@/src/services/workspaceRbac"
import type { AuthStore } from "../types"

// Zod schemas for validation
const UserRoleSchema = z.enum(USER_ROLES)

const WorkspaceInfoSchema = z.object({
    id: z.string(),
    name: z.string(),
    logoUrl: z.string(),
    websiteUrl: z.string(),
    aboutWorkspace: z.string(),
    roleName: UserRoleSchema,
    reportingManagerId: z.string().nullable(),
})

const UserTypeSchema = z.object({
    id: z.string(),
    createdDate: z.string(),
    lastModifiedDate: z.string(),
    profileImageUrl: z.string().nullable(),
    fullName: z.string(),
    email: z.string().email(),
    phoneNumber: z.string(),
    status: z.string(),
    onboardingCompleted: z.boolean(),
    role: UserRoleSchema,
    workspaces: z.array(WorkspaceInfoSchema),
    activeWorkspace: WorkspaceInfoSchema,
    permissions: z.array(z.string()),
})

const PartialUserTypeSchema = UserTypeSchema.partial()

export const useAuthStore = create<AuthStore>()(
    immer((set, get) => ({
        // State
        user: null,
        loading: true,
        error: null,
        authenticated: false,
        internalUserAccessDenied: false,

        // Actions
        setUser: (userData: Partial<UserType>) => {
            set((state) => {
                if (state.user) {
                    state.user = { ...state.user, ...userData } as UserType
                }
                state.error = null
            })
        },

        checkUserSession: async (refreshTokenOnExpire = true) => {
            set((state) => {
                state.loading = true
                state.error = null
                state.internalUserAccessDenied = false
            })

            try {
                const accessToken = localStorage.getItem("jwt_access_token")
                const isInternalUserPath =
                    typeof window !== "undefined" && window.location.pathname.startsWith("/internal-user")

                if (accessToken) {
                    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`
                }

                if (accessToken && isValidToken(accessToken)) {
                    setSession(accessToken)

                    if (isInternalUserPath) {
                        const res = await axios.get(apiUrls.ADMIN_ME())
                        const data = res.data?.data as
                            | {
                                  userId?: string
                                  fullName?: string
                                  email?: string
                                  internalWorkspaces?: Array<{
                                      workspaceId: string
                                      workspaceName: string
                                      logoUrl: string
                                  }>
                              }
                            | undefined
                        const workspacesList = data?.internalWorkspaces ?? []

                        if (workspacesList.length > 0) {
                            const workspaces: WorkspaceInfo[] = workspacesList.map((w) => ({
                                id: w.workspaceId,
                                name: w.workspaceName,
                                logoUrl: w.logoUrl ?? "",
                                websiteUrl: "",
                                aboutWorkspace: "",
                                roleName: ROLES.INTERNAL,
                                reportingManagerId: null,
                            }))
                            const newUser: UserType = {
                                id: data?.userId ?? "",
                                createdDate: "",
                                lastModifiedDate: "",
                                profileImageUrl: null,
                                fullName: data?.fullName ?? "",
                                email: data?.email ?? "",
                                phoneNumber: "",
                                status: "ACTIVE",
                                onboardingCompleted: true,
                                role: ROLES.INTERNAL,
                                workspaces,
                                activeWorkspace: workspaces[0],
                                permissions: [],
                            }
                            const activeWorkspaceId = localStorage.getItem(CURRENT_WORKSPACE_ID_STORAGE_KEY)
                            const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId) ?? workspaces[0]
                            newUser.activeWorkspace = activeWorkspace
                            localStorage.setItem(CURRENT_WORKSPACE_ID_STORAGE_KEY, activeWorkspace.id)
                            localStorage.setItem(CURRENT_WORKSPACE_ROLE_STORAGE_KEY, activeWorkspace.roleName)

                            set((state) => {
                                state.user = newUser
                                state.authenticated = true
                                state.loading = false
                                state.error = null
                            })
                            if (newUser.id) {
                                Mixpanel.identify(newUser.id)
                                Mixpanel.people.set({
                                    userId: newUser.id,
                                    $last_login: new Date(),
                                })
                            }
                        } else {
                            localStorage.removeItem("jwt_access_token")
                            localStorage.removeItem("refresh_token")
                            localStorage.removeItem(CURRENT_WORKSPACE_ID_STORAGE_KEY)
                            localStorage.removeItem(CURRENT_WORKSPACE_ROLE_STORAGE_KEY)
                            set((state) => {
                                state.user = null
                                state.authenticated = false
                                state.loading = false
                                state.internalUserAccessDenied = true
                            })
                            Mixpanel.reset()
                        }
                    } else {
                        const res = await axios.get(apiUrls.AUTH_ME)
                        const user = res.data?.data as UserType
                        const activeWorkspaceId = localStorage.getItem(CURRENT_WORKSPACE_ID_STORAGE_KEY)
                        const activeWorkspace = user.workspaces?.find((w) => w.id === activeWorkspaceId) ?? user.workspaces[0]
                        const workspaceMe = await getWorkspaceMe(activeWorkspace.id)
                        let newUser: UserType = {
                            ...user,
                            activeWorkspace,
                            permissions: [],
                        }
                        if (workspaceMe) {
                            newUser = applyWorkspaceMeToUser(newUser, workspaceMe)
                        }
                        localStorage.setItem(CURRENT_WORKSPACE_ID_STORAGE_KEY, newUser.activeWorkspace.id)
                        localStorage.setItem(CURRENT_WORKSPACE_ROLE_STORAGE_KEY, newUser.activeWorkspace.roleName)
                        set((state) => {
                            state.user = newUser
                            state.authenticated = true
                            state.loading = false
                            state.error = null
                        })
                        if (newUser?.id) {
                            Mixpanel.identify(newUser.id)
                            Mixpanel.people.set({
                                userId: newUser.id,
                                $last_login: new Date(),
                            })
                        }
                    }
                } else if (refreshTokenOnExpire) {
                    const isSuccess = await getTokenFromRefresh()
                    if (isSuccess) {
                        await get().checkUserSession(false)
                    } else {
                        set((state) => {
                            state.user = null
                            state.authenticated = false
                            state.loading = false
                        })
                    }
                } else {
                    set((state) => {
                        state.user = null
                        state.authenticated = false
                        state.loading = false
                    })
                }
            } catch (error) {
                console.error("Auth session check failed:", error)
                set((state) => {
                    state.user = null
                    state.authenticated = false
                    state.loading = false
                    state.error = error instanceof Error ? error.message : "Authentication failed"
                })
            }
        },

        switchWorkspace: (workspaceId: string) => {
            const { user } = get()
            if (!user) return

            const workspace = user.workspaces.find((w) => w.id === workspaceId)
            if (!workspace) {
                set((state) => {
                    state.error = "Workspace not found"
                })
                return
            }

            localStorage.setItem(CURRENT_WORKSPACE_ID_STORAGE_KEY, workspace.id)
            localStorage.setItem(CURRENT_WORKSPACE_ROLE_STORAGE_KEY, workspace.roleName)

            set((state) => {
                if (state.user) {
                    state.user.activeWorkspace = workspace
                }
                state.error = null
            })

            window.location.href = "/dashboard"
        },

        logout: () => {
            localStorage.removeItem("jwt_access_token")
            localStorage.removeItem("refresh_token")
            localStorage.removeItem(CURRENT_WORKSPACE_ID_STORAGE_KEY)
            localStorage.removeItem(CURRENT_WORKSPACE_ROLE_STORAGE_KEY)

            set((state) => {
                state.user = null
                state.authenticated = false
                state.loading = false
                state.error = null
                state.internalUserAccessDenied = false
            })

            // Clear Mixpanel
            Mixpanel.reset()
        },
    }))
)
